/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import ManagedAccordionSection from "@eisinc/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import CheckboxGroup, { CheckboxGroupProps } from "@eisinc/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    facetsWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                border-color: ${getColor("common.ui2")};
            `,
        },
        headerProps: {
            css: css`
                background: ${getColor("common.ui3")};
                height: 51px;
                button {
                    padding: 15px;
                    background: none;
                    &,
                    &:focus {
                        padding: 15px !important;
                        border-width: 1px !important;
                        border-color: ${getColor("common.ui2")} !important;
                    }

                    &[aria-expanded="true"] {
                        border-bottom-width: 0 !important;
                    }

                    span:not(.toggle) {
                        font-size: 18px;
                        font-weight: 600;
                        line-height: 28px;
                    }

                    span.toggle {
                        border-radius: 50%;
                        background: ${getColor("common.ui2")};
                        width: 24px;
                        height: 24px;
                        display: flex;
                        justify-content: center;
                        align-items: center;

                        svg {
                            width: 18px;
                            height: 18px;
                            color: ${getColor("secondary")};
                        }
                    }
                }
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                margin-bottom: 14px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                border-radius: 0;
                width: 18px;
                height: 18px;
                border: 2px solid ${getColor("text")};
                &:hover,
                &:focus {
                    cursor: pointer;
                    box-shadow: none;
                }

                svg {
                    width: 18px;
                    height: 18px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
                margin-left: 4px;
                font-size: 14px;
                letter-spacing: 0.3px;
                line-height: 18px;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                border-radius: 0;
                width: 18px;
                height: 18px;
                border: 2px solid ${getColor("text")};
                &:hover,
                &:focus {
                    cursor: pointer;
                    box-shadow: none;
                }
                svg {
                    width: 18px;
                    height: 18px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
                margin-left: 4px;
                font-size: 14px;
                letter-spacing: 0.3px;
                line-height: 18px;
            `,
        },
    },
    seeAllLink: {
        css: css`
            margin-top: 15px;
            color: ${getColor("text")};
            font-size: 16px;
            letter-spacing: 0;
            line-height: 19px;
        `,
    },
    seeLessLink: {
        css: css`
            margin-top: 15px;
            color: ${getColor("text")};
            font-size: 16px;
            letter-spacing: 0;
            line-height: 19px;
        `,
    },
    facetsWrapper: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 160px;

            &.facets-collapsed {
                max-height: 300px;
            }
        `,
    },
    wrapper: {
        css: css`
            margin-bottom: 20px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    onChangeFacetKnowledge: (facet: FacetModel, title: string, facets: any) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    fromKnowledgeCenter?: boolean;
    expandByDefault: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customSettings: getSettingsCollection(state).customSettings,
    };
};

type OwnProps = Props & ReturnType<typeof mapStateToProps>;

const ProductListFiltersAccordionSection: FC<OwnProps> = ({
    title,
    facets,
    fromKnowledgeCenter,
    onChangeFacet,
    onChangeFacetKnowledge,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    customSettings,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    showMoreLimit = (customSettings as any).maximumAttributeValues;

    let limitedFacets = expanded ? [...facets] : [...facets]?.sort((a, b) => b.count - a.count).slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    const orderItemsByCount = () => {
        const tempFacets = [...facets];
        tempFacets.sort((a, b) => b.count - a.count);
        limitedFacets = tempFacets?.slice(0, showMoreLimit);
    };

    const orderItemsAlphabetical = () => {
        const tempFacets = [...facets];
        limitedFacets = tempFacets;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
            >
                <StyledWrapper {...styles.facetsWrapper} className={expanded ? "facets-expanded" : "facets-collapsed"}>
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={Math.random()} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => {
                                    if (fromKnowledgeCenter) {
                                        onChangeFacetKnowledge(facet, title, facets);
                                    } else {
                                        onChangeFacet(facet);
                                    }
                                }}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {facet.name} {facet.count && facet.count !== -1 && `(${facet.count})`}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                </StyledWrapper>
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link
                            {...styles.seeLessLink}
                            onClick={() => {
                                orderItemsByCount();
                                setExpanded(false);
                            }}
                        >
                            {translate("Show Less")}
                        </Link>
                    ) : (
                        <Link
                            {...styles.seeAllLink}
                            onClick={() => {
                                orderItemsAlphabetical();
                                setExpanded(true);
                            }}
                        >
                            {translate("Show More")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListFiltersAccordionSection);
